import {
  CLINICIAN_READING_LABEL,
  EARLIER_LAB_STATUS_LABELS,
  MEASUREMENT_REVIEW_STATUS_LABEL,
  type EarlierLabEvidence,
  type EvidenceSummary,
  type PatientGroup,
} from "@care-by-ella/contracts";
import type { StatusTone } from "@care-by-ella/ui-tokens";
import { MEASUREMENT_REVIEW_TONE } from "./measurement-actions";

// TES-1034 (Emelie, point 4) put every row of "Inskickat medicinskt underlag"
// in a tag. TES-1037 gives the tags five separate meanings (status-tone.ts)
// and removes the duplicate warnings:
//
//   "Om inget tidigare provsvar har laddats upp räcker den primära statusen: Tidigare provsvar: Saknas"
//
// so without an uploaded lab result the date/source and age rows are
// subordinate and neutral ("ej bedömbara"), not three more missing items.
// With an upload but no date, the date row is incomplete (yellow) and the age,
// which follows from it, is neutral "Kan inte bedömas".
//
// Facts only, as before (TES-949): a tag says whether something is there,
// never whether it is good. A known age is "complete" because the fact is
// complete — the number itself carries no threshold (Ö-235).
//
// No row here ever uses the "risk" tone: missing evidence is not a clinical
// risk flag. The test pins that.
//
// Pure (no React), so `node --test` can run it.

export interface EvidenceTag {
  tone: StatusTone;
  text: string;
  // TES-1056 — the doctor's assessment of her blood pressure / pulse, which
  // the card renders as the doctor's control instead of a plain tag.
  review?: true;
}

export interface EvidenceStatusRow {
  key:
    | "prescription"
    | "priorLabs"
    | "sampleInfo"
    | "sampleAge"
    | "bloodPressurePulse";
  label: string;
  tags: EvidenceTag[];
  // Follows from another row's status and cannot be assessed on its own —
  // rendered quieter so it does not read as a separate finding.
  subordinate: boolean;
  // Known facts shown under the tags (the sample date and source).
  detail: string | null;
}

export function dateSv(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE");
}

export function sampleAgeText(days: number): string {
  if (days === 0) return "idag";
  if (days === 1) return "1 dag sedan";
  if (days < 60) return `${days} dagar sedan`;
  const months = Math.floor(days / 30.44);
  return `${days} dagar sedan (cirka ${months} mån)`;
}

const tag = (tone: StatusTone, text: string): EvidenceTag => ({ tone, text });

const NOT_ASSESSABLE = "Kan inte bedömas";

const uploaded = (n: number): EvidenceTag =>
  n > 0 ? tag("complete", "Uppladdat") : tag("missing", "Saknas");

function sampleInfoTags(
  sampledAt: string | null,
  source: string | null,
): EvidenceTag[] {
  if (sampledAt && source) return [tag("complete", "Komplett")];
  const tags: EvidenceTag[] = [];
  if (!sampledAt) tags.push(tag("incomplete", "Datum saknas"));
  if (!source) tags.push(tag("incomplete", "Källa saknas"));
  return tags;
}

// TES-1056 (TES-1054 point F) — a reading she reported is not "complete"
// just because it is there. Emelie:
// "Patientens värden ska inte märkas Verifierade/inte verifierade."
// The values and the doctor's assessment share one tone: unassessed
// ("Patientrapporterat") is neutral, "Granskat som medicinskt underlag" is
// complete, "Behöver ny mätning" is missing. His own reading is neutral and
// named as his. A missing half is still its own incomplete tag.
function bloodPressurePulseTags(
  bp: EvidenceSummary["bloodPressurePulse"],
): EvidenceTag[] {
  if (!bp) return [tag("missing", "Värden saknas")];
  const hasBp = bp.systolic !== null;
  const hasPulse = bp.pulse !== null;
  const parts = [
    hasBp ? `${bp.systolic}/${bp.diastolic ?? "—"} mmHg` : null,
    hasPulse ? `puls ${bp.pulse}` : null,
    `mätt ${dateSv(bp.measuredAt)}`,
  ].filter((p): p is string => p !== null);
  const values = parts.join(" · ");
  const status = bp.reviewStatus ?? "patient_reported";
  const tags =
    bp.source === "clinician"
      ? [tag("neutral", values), tag("neutral", CLINICIAN_READING_LABEL)]
      : [
          tag(MEASUREMENT_REVIEW_TONE[status], values),
          {
            ...tag(
              MEASUREMENT_REVIEW_TONE[status],
              MEASUREMENT_REVIEW_STATUS_LABEL[status],
            ),
            review: true as const,
          },
        ];
  if (hasBp && hasPulse) return tags;
  // A reading with one half: what is missing.
  return [
    ...tags,
    tag("incomplete", hasBp ? "Puls saknas" : "Blodtryck saknas"),
  ];
}

// TES-1125 — who marked an earlier lab result assessed, and when. Our
// wording (flagged for Emelie in TES-475).
export function assessedByText(
  name: string | null | undefined,
  iso: string,
): string {
  return `Bedömd av ${name ?? "läkare"} ${dateSv(iso)}`;
}

// TES-1125 — a new-treatment patient's earlier lab results read as one of
// Emelie's statuses, verbatim, instead of "Uppladdat"/"Saknas". TES-1136
// (Ö-357 a) adds a fifth, "Patienten är osäker på om tidigare provsvar
// finns.", neutral like the others and never shown as missing evidence.
// "Patienten uppger att tidigare provsvar kan finnas – ingen fil uppladdad"
// is information, not missing required evidence, so no status here is
// "missing" (or "risk"): the three before the doctor's assessment are
// neutral, and his assessment is complete, like an assessed reading.
function earlierLabTags(e: EarlierLabEvidence): EvidenceTag[] {
  return [
    tag(
      e.status === "assessed" ? "complete" : "neutral",
      EARLIER_LAB_STATUS_LABELS[e.status],
    ),
  ];
}

export function evidenceStatusRows(
  summary: EvidenceSummary,
): EvidenceStatusRow[] {
  const labs = summary.priorLabResults;
  const hasLabUpload = labs.uploads.length > 0;
  const known = [labs.sampledAt, labs.source].filter(Boolean).join(" · ");
  const earlier = summary.earlierLabs ?? null;
  return [
    {
      key: "prescription",
      label: "Dokumentation av recept/ordination",
      tags: [uploaded(summary.prescriptionDocumentation.uploads.length)],
      subordinate: false,
      detail: null,
    },
    {
      key: "priorLabs",
      label: "Tidigare provsvar",
      tags: earlier ? earlierLabTags(earlier) : [uploaded(labs.uploads.length)],
      subordinate: false,
      detail:
        earlier?.status === "assessed" && earlier.assessedAt
          ? assessedByText(earlier.assessedByName, earlier.assessedAt)
          : null,
    },
    {
      key: "sampleInfo",
      label: "Provdatum och provkälla",
      tags: hasLabUpload
        ? sampleInfoTags(labs.sampledAt, labs.source)
        : [tag("neutral", NOT_ASSESSABLE)],
      subordinate: !hasLabUpload,
      detail: known || null,
    },
    {
      key: "sampleAge",
      label: "Provsvarets ålder",
      tags: [
        labs.ageDays !== null
          ? tag(
              hasLabUpload ? "complete" : "neutral",
              sampleAgeText(labs.ageDays),
            )
          : // Emelie's example for an upload without a date: the age cannot
            // be assessed, and the date row already says why.
            tag("neutral", NOT_ASSESSABLE),
      ],
      subordinate: !hasLabUpload,
      detail: null,
    },
    {
      key: "bloodPressurePulse",
      label: "Blodtryck och vilopuls",
      tags: bloodPressurePulseTags(summary.bloodPressurePulse),
      subordinate: false,
      detail: null,
    },
  ];
}

// ---------------------------------------------------------------------------
// TES-1329 (TES-1327 §3–6, Emelie 2026-10-13) — "Underlag inför bedömningen"
// ---------------------------------------------------------------------------
//
// The same facts, read by the patient's journey. Before the doctor's
// assessment nothing a new-treatment patient hands over is required:
//   "Material som patienten kan lämna i F-02 är frivilligt och ska därför inte beskrivas som “saknat” eller som en “komplettering”."
// so her block is "Frivilligt underlag", and an absent item reads
// "Patienten har inte uppgett …", never "Saknas". Only the transfer track has
// a required document, and only there may it be "Saknas":
//   "Ordet saknas ska bara användas när något verkligen är obligatoriskt eller uttryckligen har begärts."
// The sample date/source and age rows exist only when there is an uploaded
// earlier lab result to assess:
//   "Raderna Provdatum och provkälla samt Provsvarets ålder ska bara visas när ett tidigare provsvar faktiskt finns att bedöma."

export const NO_VOLUNTARY_EVIDENCE =
  "Patienten har inte skickat in något frivilligt underlag inför vårdmötet.";
const NOT_STATED_PRESCRIPTION =
  "Patienten har inte uppgett något tidigare recept eller någon ordination.";
const NOT_STATED_LABS = "Patienten har inte uppgett tidigare provsvar.";
const NOT_STATED_BP =
  "Patienten har inte uppgett tidigare blodtrycks- eller vilopulsvärden.";

// The part of a transfer review that decides the required document's status.
export interface TransferReviewState {
  status: string;
  requestedCompletionGroups: readonly string[];
}

export interface EvidenceBlock {
  key: "voluntary" | "mandatory";
  heading: string;
  rows: EvidenceStatusRow[];
  // Shown instead of the rows when there are none.
  emptyText: string | null;
}

// "Obligatoriskt underlag för receptövertag": per required document, one of
// "har skickats in, behöver granskas, inte uppfyller kraven eller fortfarande
// saknas". F-07 requires at least one prescription document; the review says
// which of the four it is. "Uppfyller inte kraven" is the doctor's own
// verdict: he asked her to redo exactly this part (`prescription_evidence`).
export function transferPrescriptionTag(
  uploads: number,
  review: TransferReviewState | null | undefined,
): EvidenceTag {
  if (uploads === 0) return tag("missing", "Saknas");
  if (
    review?.status === "completion_requested" &&
    review.requestedCompletionGroups.includes("prescription_evidence")
  ) {
    return tag("incomplete", "Uppfyller inte kraven");
  }
  if (review?.status === "pending_review") {
    return tag("incomplete", "Behöver granskas");
  }
  return tag("complete", "Inskickat");
}

// Earlier lab results, blood pressure and resting pulse: voluntary on both
// tracks before the doctor's assessment (F-07 too: "Tidigare provsvar,
// blodtryck och vilopuls är frivilliga före inskick").
function voluntaryRows(summary: EvidenceSummary): EvidenceStatusRow[] {
  const legacy = evidenceStatusRows(summary);
  const row = (k: EvidenceStatusRow["key"]) => legacy.find((r) => r.key === k)!;
  const hasLabUpload = summary.priorLabResults.uploads.length > 0;
  const rows: EvidenceStatusRow[] = [
    {
      ...row("priorLabs"),
      // Without F-02's earlier-lab answer (the transfer track, or an older
      // F-02), an absent file is "not stated" rather than "Saknas".
      tags:
        summary.earlierLabs || hasLabUpload
          ? row("priorLabs").tags
          : [tag("neutral", NOT_STATED_LABS)],
    },
  ];
  if (hasLabUpload) rows.push(row("sampleInfo"), row("sampleAge"));
  rows.push({
    ...row("bloodPressurePulse"),
    tags: summary.bloodPressurePulse
      ? row("bloodPressurePulse").tags
      : [tag("neutral", NOT_STATED_BP)],
  });
  return rows;
}

// Has she handed over anything at all? Her F-02 answer that she has no
// earlier lab results is not something handed over.
function hasAnyEvidence(summary: EvidenceSummary): boolean {
  const earlier = summary.earlierLabs ?? null;
  return (
    summary.prescriptionDocumentation.uploads.length > 0 ||
    summary.priorLabResults.uploads.length > 0 ||
    (earlier !== null && earlier.status !== "not_stated") ||
    summary.bloodPressurePulse !== null
  );
}

export function evidenceBlocks(
  summary: EvidenceSummary,
  group: PatientGroup,
  review?: TransferReviewState | null,
): EvidenceBlock[] {
  const prescriptionUploads = summary.prescriptionDocumentation.uploads.length;
  if (group === "PRESCRIPTION_TRANSFER") {
    return [
      {
        key: "mandatory",
        heading: "Obligatoriskt underlag för receptövertag",
        rows: [
          {
            key: "prescription",
            label: "Dokumentation av recept/ordination",
            tags: [transferPrescriptionTag(prescriptionUploads, review)],
            subordinate: false,
            detail: null,
          },
        ],
        emptyText: null,
      },
      {
        key: "voluntary",
        heading: "Frivilligt underlag",
        rows: voluntaryRows(summary),
        emptyText: null,
      },
    ];
  }
  return [
    {
      key: "voluntary",
      heading: "Frivilligt underlag",
      rows: hasAnyEvidence(summary)
        ? [
            {
              key: "prescription",
              label: "Tidigare recept eller ordination",
              tags: [
                prescriptionUploads > 0
                  ? tag("complete", "Uppladdat")
                  : tag("neutral", NOT_STATED_PRESCRIPTION),
              ],
              subordinate: false,
              detail: null,
            },
            ...voluntaryRows(summary),
          ]
        : [],
      emptyText: NO_VOLUNTARY_EVIDENCE,
    },
  ];
}
